import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import "./TenantContacts.css";
import { API_BASE } from "../../../ApiUrl";
import TenantPeopleTabs from "./TenantPeopleTabs";
import OrganizationTabs from "../Organizations/OrganizationTabs";

const AuthorizedRepresentatives = () => {
  const navigate = useNavigate();

  const [representatives, setRepresentatives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

const fetchRepresentatives = useCallback(async () => {
  setLoading(true);
  setError("");

  try {
    const storedTenantUser =
      localStorage.getItem("tenant_user");

    if (!storedTenantUser) {
      throw new Error("Tenant information missing.");
    }

    const tenantUser = JSON.parse(storedTenantUser);

    if (!tenantUser.tenant_id) {
      throw new Error("Tenant ID missing.");
    }

    const response = await fetch(
      `${API_BASE}/tenant-records/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          tenant_id: Number(tenantUser.tenant_id),
          tables: [
            "tenant_authorised_representatives",
          ],
        }),
      }
    );

    if (!response.ok) {
      throw new Error(
        `HTTP error! status: ${response.status}`
      );
    }

    const result = await response.json();

    console.log(
      "Tenant authorised representatives response:",
      result
    );

    let data = result;

    if (result?.data) {
      data = result.data;
    }

    // Handle table-wise response
    if (data?.tenant_authorised_representatives) {
      data = data.tenant_authorised_representatives;
    }

    if (Array.isArray(data)) {
      setRepresentatives(data);
    } else {
      setRepresentatives([]);
    }
  } catch (error) {
    console.error(
      "Error fetching authorised representatives:",
      error
    );

    setError(
      error.message ||
        "Failed to load authorised representatives."
    );

    setRepresentatives([]);
  } finally {
    setLoading(false);
  }
}, []);

  useEffect(() => {
    fetchRepresentatives();
  }, [fetchRepresentatives]);

  const handleAddClick = () => {
    navigate("/admin/auth-rep/add");
  };

  const handleEdit = (representative) => {
    navigate("/admin/auth-rep/add", {
      state: {
        editRepresentative: representative
      }
    });
  };

  const handleDelete = async (representative) => {
    const confirmResult = await Swal.fire({
      icon: "warning",
      title: "Delete Authorised Representative?",
      html: `Are you sure you want to delete <strong>${
        representative.full_name || "this representative"
      }</strong>?<br/>This action cannot be undone.`,
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      reverseButtons: true
    });

    if (!confirmResult.isConfirmed) return;

    Swal.fire({
      title: "Deleting...",
      text: "Please wait while the representative is being deleted.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const response = await fetch(
        `${API_BASE}/tenant-authorised-representatives/${representative.id}/`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json"
          }
        }
      );

      const data =
        response.status === 204
          ? null
          : await response.json().catch(() => null);

      if (!response.ok) {
        let message = "";

        if (
          data?.errors &&
          typeof data.errors === "object"
        ) {
          message = Object.entries(data.errors)
            .map(
              ([field, errors]) =>
                `${field}: ${
                  Array.isArray(errors)
                    ? errors.join(", ")
                    : errors
                }`
            )
            .join(" | ");
        } else {
          message =
            data?.message ||
            data?.detail ||
            `Request failed with status ${response.status}`;
        }

        throw new Error(message);
      }

      setRepresentatives((prev) =>
        prev.filter(
          (item) => item.id !== representative.id
        )
      );

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Authorised representative has been deleted successfully.",
        confirmButtonColor: "#3085d6",
        timer: 1800,
        timerProgressBar: true,
        showConfirmButton: false
      });

      fetchRepresentatives();
    } catch (error) {
      console.error(
        "Error deleting authorised representative:",
        error
      );

      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text:
          error.message ||
          "Failed to delete authorised representative.",
        confirmButtonColor: "#d33"
      });
    }
  };

  const getAuthorityTypeLabel = (type) => {
    switch (type) {
      case "DIRECTOR":
        return "Director";

      case "POWER_OF_ATTORNEY":
        return "Power of Attorney";

      case "DESIGNATED_SIGNATORY":
        return "Designated Signatory";

      default:
        return type || "—";
    }
  };

  const getVerificationStatusLabel = (status) => {
    switch (status) {
      case "SELF_DECLARED":
        return "Self Declared";

      case "VERIFIED":
        return "Verified";

      case "PENDING":
        return "Pending";

      case "REJECTED":
        return "Rejected";

      default:
        return status || "—";
    }
  };

  const formatDate = (date) => {
    if (!date) return "—";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return date;
    }

    return parsedDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="tenant-contacts-page">
          <OrganizationTabs/>
           <TenantPeopleTabs />
          <div className="tenant-contacts-header">
            <div>
              <h2>Authorised Representatives</h2>

              <p>
                Manage tenant authorised representatives and
                their validity.
              </p>
            </div>

            <button
              type="button"
              className="tenant-contacts-add-btn"
              onClick={handleAddClick}
            >
              <i className="fas fa-plus"></i>
              Add Authorised Representative
            </button>
          </div>
          

          {error && (
            <div className="tenant-contacts-error">
              {error}
            </div>
          )}

          <div className="tenant-contacts-table-container">
            <table className="tenant-contacts-table">
              <colgroup>
                <col className="contact-col-number" />
                <col className="contact-col-name" />
                <col className="contact-col-type" />
                <col className="contact-col-details" />
                <col className="contact-col-date" />
                <col className="contact-col-date" />
                <col className="contact-col-status" />
                <col className="contact-col-actions" />
              </colgroup>

              <thead>
                <tr>
                  <th>#</th>
                  <th>Full Name</th>
                  <th>Authority Type</th>
                  <th>Representative Details</th>
                  <th>Effective From</th>
                  <th>Effective To</th>
                  <th>Verification Status</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr>
                    <td
                      colSpan="8"
                      className="tenant-contacts-empty-cell"
                    >
                      Loading authorised representatives...
                    </td>
                  </tr>
                )}

                {!loading &&
                  representatives.length === 0 && (
                    <tr>
                      <td
                        colSpan="8"
                        className="tenant-contacts-empty-cell"
                      >
                        No authorised representatives found.
                      </td>
                    </tr>
                  )}

                {!loading &&
                  representatives.map(
                    (representative, index) => (
                      <tr key={representative.id}>
                        <td className="tenant-contacts-number-cell">
                          {index + 1}
                        </td>

                        <td className="tenant-contacts-name-cell">
                          <div>
                            <div>
                              {representative.full_name || "—"}
                            </div>

                            {representative.title && (
                              <small>
                                {representative.title}
                              </small>
                            )}
                          </div>
                        </td>

                        <td>
                          <span className="tenant-contacts-type-badge">
                            {getAuthorityTypeLabel(
                              representative.authority_type
                            )}
                          </span>
                        </td>

                        <td>
                          <div className="tenant-contacts-details">
                            <div className="tenant-contacts-detail-row">
                              <i className="fas fa-envelope"></i>

                              <span
                                title={
                                  representative.official_email ||
                                  ""
                                }
                              >
                                {representative.official_email ||
                                  "—"}
                              </span>
                            </div>

                            <div className="tenant-contacts-detail-row">
                              <i className="fas fa-phone"></i>

                              <span
                                title={
                                  representative.mobile || ""
                                }
                              >
                                {representative.mobile || "—"}
                              </span>
                            </div>
                          </div>
                        </td>

                        <td className="tenant-contacts-date-cell">
                          {formatDate(
                            representative.effective_from
                          )}
                        </td>

                        <td className="tenant-contacts-date-cell">
                          {formatDate(
                            representative.effective_to
                          )}
                        </td>

                        <td>
                          <span className="tenant-contacts-status-badge tenant-contacts-status-active">
                            {getVerificationStatusLabel(
                              representative.verification_status
                            )}
                          </span>
                        </td>

                        <td>
                          <div className="tenant-contacts-actions">
                            <button
                              type="button"
                              className="tenant-contacts-action-btn tenant-contacts-edit-btn"
                              title="Edit"
                              onClick={() =>
                                handleEdit(representative)
                              }
                            >
                              <i className="fas fa-pen"></i>
                            </button>

                            <button
                              type="button"
                              className="tenant-contacts-action-btn tenant-contacts-delete-btn"
                              title="Delete"
                              onClick={() =>
                                handleDelete(representative)
                              }
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthorizedRepresentatives;